import { useCallback } from 'react';
import {
    AUTOMATIC_PRESENTATIONS,
    type AutomaticPresentationEligibility,
    useAutomaticPresentation,
} from '#shared/lib/automaticPresentation';
import { DASHBOARD_TODO_LIST_FEATURE_HINT_IDS, type DashboardTodoListFeatureHintId } from '#shared/lib/featureHints';
import {
    FEATURE_HINT_DASHBOARD_TODO_COMPLETION_ICON_KEY,
    FEATURE_HINT_DASHBOARD_TODO_NEW_LABEL_KEY,
    FEATURE_HINT_DASHBOARD_TODO_TITLE_KEY,
    usePersistentStorage,
} from '#shared/lib/storage';

const DISMISSED_STORAGE_VALUE = '1';

export type DashboardTodoListFeatureHintEligibility = Record<
    DashboardTodoListFeatureHintId,
    AutomaticPresentationEligibility
>;

type DashboardTodoListFeatureHintFlags = Readonly<Record<DashboardTodoListFeatureHintId, boolean>>;

/**
 * Допускает к общей очереди только первую ещё не закрытую Dashboard-подсказку.
 * Следующий шаг цепочки становится доступен после того, как закрытие предыдущего сохранено в localStorage.
 *
 * @kind helper
 * @returns Статусы допуска с учётом сохранённой последовательности подсказок.
 */
function getSequentialHintEligibility(
    hintEligibility: DashboardTodoListFeatureHintEligibility,
    dismissedHints: DashboardTodoListFeatureHintFlags
): DashboardTodoListFeatureHintEligibility {
    const titleDismissed = dismissedHints[DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.TITLE];
    const completionIconDismissed = dismissedHints[DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.COMPLETION_ICON];
    const newLabelDismissed = dismissedHints[DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.NEW_LABEL];

    return {
        [DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.TITLE]: titleDismissed
            ? 'ineligible'
            : hintEligibility[DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.TITLE],
        [DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.COMPLETION_ICON]:
            titleDismissed && !completionIconDismissed
                ? hintEligibility[DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.COMPLETION_ICON]
                : 'ineligible',
        [DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.NEW_LABEL]:
            titleDismissed && completionIconDismissed && !newLabelDismissed
                ? hintEligibility[DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.NEW_LABEL]
                : 'ineligible',
    };
}

/**
 * Преобразует флаги состояния в id, который UI использует для показа или анимации закрытия подсказки.
 *
 * @kind helper
 * @returns Id текущей подсказки или `null`, если ни одна не находится в переданном состоянии.
 */
function getCurrentHintId(hintStates: DashboardTodoListFeatureHintFlags): DashboardTodoListFeatureHintId | null {
    if (hintStates[DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.TITLE]) {
        return DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.TITLE;
    }

    if (hintStates[DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.COMPLETION_ICON]) {
        return DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.COMPLETION_ICON;
    }

    if (hintStates[DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.NEW_LABEL]) {
        return DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.NEW_LABEL;
    }

    return null;
}

/**
 * Сохраняет продуктовую цепочку и localStorage-состояние Dashboard-подсказок,
 * а право на фактический показ получает у общего координатора автоматических поверхностей.
 * @kind helper
 * @returns Активную подсказку и функции для начала и завершения её закрытия.
 */
export function useDashboardTodoListFeatureHints(hintEligibility: DashboardTodoListFeatureHintEligibility): {
    activeHintId: DashboardTodoListFeatureHintId | null;
    closingHintId: DashboardTodoListFeatureHintId | null;
    dismiss: (hintId: DashboardTodoListFeatureHintId) => void;
    completeClosing: (hintId: DashboardTodoListFeatureHintId) => void;
} {
    const [titleDismissedValue, setTitleDismissedValue] = usePersistentStorage<'1'>(
        FEATURE_HINT_DASHBOARD_TODO_TITLE_KEY
    );
    const [completionIconDismissedValue, setCompletionIconDismissedValue] = usePersistentStorage<'1'>(
        FEATURE_HINT_DASHBOARD_TODO_COMPLETION_ICON_KEY
    );
    const [newLabelDismissedValue, setNewLabelDismissedValue] = usePersistentStorage<'1'>(
        FEATURE_HINT_DASHBOARD_TODO_NEW_LABEL_KEY
    );

    const presentationEligibility = getSequentialHintEligibility(hintEligibility, {
        [DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.TITLE]: titleDismissedValue === DISMISSED_STORAGE_VALUE,
        [DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.COMPLETION_ICON]:
            completionIconDismissedValue === DISMISSED_STORAGE_VALUE,
        [DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.NEW_LABEL]: newLabelDismissedValue === DISMISSED_STORAGE_VALUE,
    });

    // Первая подсказка знакомит пользователя с назначением Dashboard-чек-листа.
    const {
        isActive: isTitleActive,
        isLeaving: isTitleLeaving,
        startLeaving: startTitleLeaving,
        completeLeaving: completeTitleLeaving,
    } = useAutomaticPresentation({
        ...AUTOMATIC_PRESENTATIONS.NORMAL.dashboardTodoListTitle,
        eligibility: presentationEligibility[DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.TITLE],
    });

    // Вторая подсказка объясняет, как отмечать выполненные пункты чек-листа.
    const {
        isActive: isCompletionIconActive,
        isLeaving: isCompletionIconLeaving,
        startLeaving: startCompletionIconLeaving,
        completeLeaving: completeCompletionIconLeaving,
    } = useAutomaticPresentation({
        ...AUTOMATIC_PRESENTATIONS.NORMAL.dashboardTodoListCompletionIcon,
        eligibility: presentationEligibility[DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.COMPLETION_ICON],
    });

    // Третья подсказка показывает, где появляются новые рекомендации.
    const {
        isActive: isNewLabelActive,
        isLeaving: isNewLabelLeaving,
        startLeaving: startNewLabelLeaving,
        completeLeaving: completeNewLabelLeaving,
    } = useAutomaticPresentation({
        ...AUTOMATIC_PRESENTATIONS.NORMAL.dashboardTodoListNewLabel,
        eligibility: presentationEligibility[DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.NEW_LABEL],
    });

    const dismiss = useCallback(
        (hintId: DashboardTodoListFeatureHintId) => {
            switch (hintId) {
                case DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.TITLE:
                    // Сначала удерживаем общий слот на время анимации, затем сохраняем закрытие и разрешаем следующую.
                    if (startTitleLeaving('dismissed')) {
                        setTitleDismissedValue(DISMISSED_STORAGE_VALUE);
                    }

                    return;

                case DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.COMPLETION_ICON:
                    if (startCompletionIconLeaving('dismissed')) {
                        setCompletionIconDismissedValue(DISMISSED_STORAGE_VALUE);
                    }

                    return;

                case DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.NEW_LABEL:
                    if (startNewLabelLeaving('dismissed')) {
                        setNewLabelDismissedValue(DISMISSED_STORAGE_VALUE);
                    }
            }
        },
        [
            setCompletionIconDismissedValue,
            setNewLabelDismissedValue,
            setTitleDismissedValue,
            startCompletionIconLeaving,
            startNewLabelLeaving,
            startTitleLeaving,
        ]
    );

    const completeClosing = useCallback(
        (hintId: DashboardTodoListFeatureHintId) => {
            switch (hintId) {
                case DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.TITLE:
                    completeTitleLeaving();

                    return;

                case DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.COMPLETION_ICON:
                    completeCompletionIconLeaving();

                    return;

                case DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.NEW_LABEL:
                    completeNewLabelLeaving();
            }
        },
        [completeCompletionIconLeaving, completeNewLabelLeaving, completeTitleLeaving]
    );

    const activeHintId = getCurrentHintId({
        [DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.TITLE]: isTitleActive,
        [DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.COMPLETION_ICON]: isCompletionIconActive,
        [DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.NEW_LABEL]: isNewLabelActive,
    });
    const closingHintId = getCurrentHintId({
        [DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.TITLE]: isTitleLeaving,
        [DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.COMPLETION_ICON]: isCompletionIconLeaving,
        [DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.NEW_LABEL]: isNewLabelLeaving,
    });

    return {
        activeHintId,
        closingHintId,
        dismiss,
        completeClosing,
    };
}
